import { useEffect, useState } from 'react';
import { FileText, Plus, X, Trash2, Send, Check, ChevronRight, Clock, Ban, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;
const FINISH_COLORS = {
  SS:  { label: 'Smooth Stainless',        swatch: '#b8b8b8' },
  GRF: { label: 'Graphite Black Cerakote', swatch: '#2a2a2a' },
  FDE: { label: 'FDE – Flat Dark Earth',   swatch: '#9E8A5A' },
};

// ── Create Quote Modal ──────────────────────────────────────────────────────
function CreateQuoteModal({ onClose, onCreated, token }) {
  const [dealers, setDealers] = useState([]);
  const [products, setProducts] = useState([]);
  const [dealerId, setDealerId] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [expiresDays, setExpiresDays] = useState(30);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/admin/dealers?status=approved`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(`${BASE}/api/admin/products`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ]).then(([d, p]) => {
      setDealers(Array.isArray(d) ? d : []);
      setProducts(Array.isArray(p) ? p.filter(x => x.nfa_item) : []);
    });
  }, [token]);

  const addItem = (product) => {
    setItems(prev => {
      const existing = prev.find(i => i.product_slug === product.slug);
      if (existing) return prev;
      const base35 = product.dealer_price || round35(product.price);
      return [...prev, {
        product_slug: product.slug, product_name: product.name,
        product_image: product.image || '', quantity: 1,
        retail_price: product.price, custom_price: base35,
        finish: 'SS', finish_label: 'Smooth Stainless', notes: '',
      }];
    });
  };

  const round35 = (retail) => Math.round(retail * 0.65 * 100) / 100;

  const setItem = (idx, key, val) => setItems(prev => {
    const n = [...prev]; n[idx] = { ...n[idx], [key]: val }; return n;
  });

  const subtotal = items.reduce((s, i) => s + (i.custom_price * i.quantity), 0);

  const handleSend = async () => {
    if (!dealerId || items.length === 0) { setError('Select a dealer and add at least one item'); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(`${BASE}/api/admin/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dealer_id: dealerId, items, notes, expires_days: +expiresDays }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      onCreated(data);
      onClose();
    } catch (e) { setError(e.message); }
    setSending(false);
  };

  const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors";

  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <h2 className="font-bebas text-xl text-white tracking-wider">CREATE CUSTOM QUOTE</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">{error}</div>}

          {/* Dealer select */}
          <div>
            <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Send Quote To *</label>
            <select value={dealerId} onChange={e => setDealerId(e.target.value)} className={`${inputCls} w-full`} required>
              <option value="">— Select Approved Dealer —</option>
              {dealers.map(d => <option key={d._id} value={d._id}>{d.business_name} ({d.email})</option>)}
            </select>
          </div>

          {/* Product picker */}
          <div>
            <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Add Products</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {products.map(p => (
                <button key={p.slug} onClick={() => addItem(p)}
                  disabled={items.some(i => i.product_slug === p.slug)}
                  className="font-inter text-xs border border-white/20 hover:border-orange-500/50 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 text-zinc-400 hover:text-white transition-colors">
                  + {p.name}
                </button>
              ))}
            </div>

            {/* Items table */}
            {items.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-[2fr_80px_100px_100px_80px_50px] gap-2 px-2 font-inter text-[9px] text-zinc-600 uppercase tracking-wider">
                  <div>Product</div><div>Qty</div><div>Retail</div><div>Your Price</div><div>Finish</div><div></div>
                </div>
                {items.map((item, idx) => {
                  const discountPct = Math.round((1 - item.custom_price / item.retail_price) * 100);
                  return (
                    <div key={idx} className="grid grid-cols-[2fr_80px_100px_100px_80px_50px] gap-2 items-center bg-[#1a1a1a] border border-white/10 px-3 py-2">
                      <div>
                        <p className="font-inter text-xs text-white">{item.product_name}</p>
                        <input value={item.notes} onChange={e => setItem(idx,'notes',e.target.value)}
                          placeholder="Note (optional)" className="w-full mt-1 bg-transparent text-[10px] text-zinc-500 border-b border-white/5 focus:border-orange-500/50 outline-none placeholder-zinc-700" />
                      </div>
                      <input type="number" min="1" value={item.quantity} onChange={e => setItem(idx,'quantity',+e.target.value)}
                        className={`${inputCls} w-full text-center`} />
                      <div className="font-inter text-sm text-zinc-500 text-center">${item.retail_price.toFixed(2)}</div>
                      <div>
                        <input type="number" step="0.01" value={item.custom_price} onChange={e => setItem(idx,'custom_price',+e.target.value)}
                          className={`${inputCls} w-full font-mono`} />
                        <p className={`font-inter text-[9px] text-center mt-0.5 ${discountPct >= 50 ? 'text-green-400' : 'text-orange-400'}`}>
                          {discountPct}% off
                        </p>
                      </div>
                      <select value={item.finish} onChange={e => { setItem(idx,'finish',e.target.value); setItem(idx,'finish_label',FINISH_COLORS[e.target.value]?.label||e.target.value); }}
                        className={`${inputCls} w-full text-xs`}>
                        {Object.entries(FINISH_COLORS).map(([k,v]) => <option key={k} value={k}>{k}</option>)}
                      </select>
                      <button onClick={() => setItems(prev => prev.filter((_,i)=>i!==idx))} className="text-zinc-700 hover:text-red-400 transition-colors mx-auto"><Trash2 size={14} /></button>
                    </div>
                  );
                })}
                <div className="flex justify-end font-bebas text-xl text-white px-2">
                  Total: <span className="text-orange-400 ml-3">${subtotal.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes + expiry */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Quote Notes (shown to dealer)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                placeholder="e.g. Demo pricing — 2 units at 50% so you can experience the product..."
                className={`${inputCls} w-full resize-none placeholder-zinc-700`} />
            </div>
            <div>
              <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Expires In (days)</label>
              <input type="number" min="1" max="365" value={expiresDays} onChange={e => setExpiresDays(e.target.value)}
                className={`${inputCls} w-full`} />
              <p className="font-inter text-[9px] text-zinc-600 mt-1">Quote auto-expires after this many days</p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 px-5 py-4 flex gap-3 flex-shrink-0">
          <button onClick={handleSend} disabled={sending || !dealerId || items.length === 0}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bebas text-lg tracking-widest px-6 py-3 transition-colors">
            <Send size={16} />{sending ? 'SENDING...' : 'SEND QUOTE TO DEALER'}
          </button>
          <button onClick={onClose} className="border border-white/20 text-zinc-400 hover:text-white font-bebas text-lg tracking-widest px-5 py-3 transition-colors">CANCEL</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Quotes Page ─────────────────────────────────────────────────────
export default function AdminQuotes() {
  const { token } = useAdminAuth();
  const [quotes, setQuotes] = useState([]);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editConfig, setEditConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  const load = async () => {
    const [q, c] = await Promise.all([
      fetch(`${BASE}/api/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(`${BASE}/api/admin/dealer-pricing-config`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : {}),
    ]);
    setQuotes(Array.isArray(q) ? q : []);
    setPricingConfig(c);
    setConfigForm(c);
    setLoading(false);
  };

  useEffect(() => { load(); }, [token]);

  const handleCancelQuote = async (quoteId) => {
    if (!window.confirm('Cancel this quote?')) return;
    await fetch(`${BASE}/api/admin/quotes/${quoteId}/cancel`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    await fetch(`${BASE}/api/admin/dealer-pricing-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...configForm, base_discount_pct: +configForm.base_discount_pct, volume_threshold: +configForm.volume_threshold, volume_discount_pct: +configForm.volume_discount_pct }),
    });
    setSavingConfig(false);
    setEditConfig(false);
    load();
  };

  const STATUS_COLORS = { pending: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10', accepted: 'text-green-400 border-green-500/30 bg-green-500/10', cancelled: 'text-zinc-600 border-zinc-700 bg-zinc-800', expired: 'text-red-400 border-red-500/30 bg-red-500/10' };
  const inputCls = "bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors";

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider flex items-center gap-3"><FileText size={24} className="text-orange-500" />QUOTES & DEALER PRICING</h1>
          <p className="font-inter text-xs text-zinc-500 mt-1">Volume pricing rules · Custom quote builder · Send personalized pricing to dealers</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} className="flex items-center gap-1.5 border border-white/20 text-zinc-400 hover:text-white font-inter text-xs px-3 py-2 transition-colors"><RefreshCw size={13} />Refresh</button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-base tracking-widest px-5 py-2.5 transition-colors">
            <Plus size={16} />NEW CUSTOM QUOTE
          </button>
        </div>
      </div>

      {/* Pricing Config */}
      {pricingConfig && (
        <div className="bg-[#1a1a1a] border border-white/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bebas text-lg text-white tracking-wider">VOLUME PRICING RULES</h2>
            <button onClick={() => setEditConfig(e => !e)} className="font-inter text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 px-3 py-1.5 transition-colors">
              {editConfig ? 'Cancel' : 'Edit Rules'}
            </button>
          </div>
          {editConfig ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Base Discount %</label>
                <input type="number" value={configForm.base_discount_pct} onChange={e => setConfigForm(p=>({...p,base_discount_pct:e.target.value}))} className={`${inputCls} w-full`} /></div>
              <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Volume Threshold (units)</label>
                <input type="number" value={configForm.volume_threshold} onChange={e => setConfigForm(p=>({...p,volume_threshold:e.target.value}))} className={`${inputCls} w-full`} /></div>
              <div><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Volume Discount %</label>
                <input type="number" value={configForm.volume_discount_pct} onChange={e => setConfigForm(p=>({...p,volume_discount_pct:e.target.value}))} className={`${inputCls} w-full`} /></div>
              <div className="flex items-end"><button onClick={saveConfig} disabled={savingConfig} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest py-2 transition-colors">
                {savingConfig ? 'SAVING...' : 'SAVE'}
              </button></div>
              <div className="col-span-full"><label className="block font-inter text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Excluded SKUs (always base rate) — comma separated</label>
                <input value={(configForm.excluded_skus||[]).join(', ')} onChange={e => setConfigForm(p=>({...p,excluded_skus:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} className={`${inputCls} w-full`} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                {label:'Base Rate', val:`${pricingConfig.base_discount_pct}% off`, color:'text-orange-400'},
                {label:'Volume Threshold', val:`${pricingConfig.volume_threshold}+ units`, color:'text-blue-400'},
                {label:'Volume Rate', val:`${pricingConfig.volume_discount_pct}% off`, color:'text-green-400'},
                {label:'Always Base Rate', val: (pricingConfig.excluded_skus||[]).length + ' products', color:'text-yellow-400'},
              ].map(s => (
                <div key={s.label} className="bg-[#0f0f0f] border border-white/5 p-3">
                  <p className="font-inter text-[9px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                  <p className={`font-bebas text-xl tracking-wider mt-1 ${s.color}`}>{s.val}</p>
                </div>
              ))}
              <div className="col-span-full font-inter text-xs text-zinc-500">
                Excluded (always {pricingConfig.base_discount_pct}% off): {(pricingConfig.excluded_skus||[]).join(', ')}
              </div>
              <div className="col-span-full text-zinc-600 font-inter text-[10px] italic">{pricingConfig.excluded_note}</div>
            </div>
          )}
        </div>
      )}

      {/* Quotes list */}
      <div>
        <h2 className="font-bebas text-xl text-white tracking-wider mb-3">SENT QUOTES ({quotes.length})</h2>
        {loading ? <div className="flex items-center justify-center h-24"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        : quotes.length === 0 ? (
          <div className="text-center py-12 bg-[#1a1a1a] border border-white/10 text-zinc-600 font-inter text-sm">
            No quotes sent yet — create your first custom quote above
          </div>
        ) : (
          <div className="space-y-2">
            {quotes.map(qt => (
              <div key={qt.quote_id} className="bg-[#1a1a1a] border border-white/10 p-4">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-bebas text-lg text-white tracking-wider">{qt.quote_id}</span>
                      <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${STATUS_COLORS[qt.status] || ''}`}>
                        {qt.status}
                      </span>
                    </div>
                    <p className="font-inter text-sm text-zinc-300">{qt.dealer_name} · {qt.dealer_email}</p>
                    <p className="font-inter text-xs text-zinc-500 mt-0.5">
                      {qt.items?.length} item{qt.items?.length !== 1 ? 's' : ''} · 
                      Total: <span className="text-orange-400 font-bold">${qt.subtotal?.toFixed(2)}</span> · 
                      By: {qt.created_by} · 
                      {qt.created_at ? new Date(qt.created_at).toLocaleDateString() : ''}
                    </p>
                    {qt.notes && <p className="font-inter text-[10px] text-zinc-600 mt-1 italic">"{qt.notes}"</p>}
                    {qt.order_number && <p className="font-inter text-[10px] text-green-400 mt-1">→ Order: {qt.order_number}</p>}
                  </div>
                  <div className="flex gap-2">
                    {qt.status === 'pending' && (
                      <button onClick={() => handleCancelQuote(qt.quote_id)}
                        className="flex items-center gap-1 font-inter text-xs text-zinc-500 hover:text-red-400 border border-white/10 hover:border-red-500/30 px-3 py-1.5 transition-colors">
                        <Ban size={12} />Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && <CreateQuoteModal token={token} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}
